import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { AuthProvider, useAuth } from './contexts/AuthContext.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { LoginPage } from './pages/LoginPage.js';
import { ConnectionProfilesPage } from './pages/ConnectionProfilesPage.js';
import { ImportProfilesPage } from './pages/ImportProfilesPage.js';
import { ImportProfileDetailPage } from './pages/ImportProfileDetailPage.js';
import { ImportRunPage } from './pages/ImportRunPage.js';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between">
          <h1 className="text-base font-semibold tracking-tight">FlixFlow Importer</h1>
          {user && (
            <div className="flex items-center gap-4">
              <nav className="flex gap-1">
                <NavItem href="/verbindungen">Verbindungen</NavItem>
                <NavItem href="/importprofile">Importprofile</NavItem>
              </nav>
              <span className="text-xs text-neutral-400">|</span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-neutral-500">{user.username}</span>
                <button
                  onClick={() => void logout()}
                  className="text-xs text-neutral-500 hover:text-neutral-800"
                >
                  Abmelden
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-neutral-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-3 text-xs text-neutral-400">
          Mittelstandsbüro AG · FlixFlow Importer
        </div>
      </footer>
    </div>
  );
}

function NavItem({ href, children }: { href: string; children: React.ReactNode }) {
  const { pathname } = useLocation();
  const active = pathname.startsWith(href);
  return (
    <Link
      to={href}
      className={`px-3 py-1.5 rounded text-sm transition-colors ${
        active
          ? 'bg-neutral-100 text-neutral-900 font-medium'
          : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
      }`}
    >
      {children}
    </Link>
  );
}

function NotFoundPage() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <h2 className="text-xl font-semibold">Seite nicht gefunden</h2>
      <p className="text-neutral-600 mt-1">Die angeforderte Seite existiert nicht.</p>
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Navigate to="/verbindungen" replace />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/verbindungen"
            element={
              <ProtectedRoute>
                <Layout>
                  <ConnectionProfilesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/importprofile"
            element={
              <ProtectedRoute>
                <Layout>
                  <ImportProfilesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/import-profile/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ImportProfileDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/import-run/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ImportRunPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <Layout>
                <NotFoundPage />
              </Layout>
            }
          />
        </Routes>
      </AuthProvider>
    </QueryClientProvider>
  );
}
